import React, { useEffect, useRef, useState } from 'react';
import ModuleFrame, { Chip } from '../ModuleFrame';
import { db } from '../../firebaseConfig';
import { createEntity, deleteEntity, friendlyError, useEntityList } from '../../data/entities';
import { uploadOnce, getAssetDownloadUrl, deleteRemoteObject } from '../../data/uploadService';
import { VOICE_SCRIPT, VIDEO_CUE, TAKE_LIMITS, pickMime, extFor, takeKey } from './likenessMime';

// Recorder for one person's own voice and face. The files are the input a
// later model would use. This screen does not build a voice or a speaking face.
export default function LikenessTake({ org, app, uid, isAdmin }) {
  const [takes, setTakes] = useState(null);
  const [listErr, setListErr] = useState('');
  const { docs: members } = useEntityList('member', { org: org.id });

  useEffect(() => {
    if (!uid) return undefined;
    let q = db.collection(`orgs/${org.id}/likenessTakes`);
    // Admins may list every take. Anyone else must constrain createdBy or
    // the rules reject the query.
    q = isAdmin ? q.orderBy('createdAt', 'desc') : q.where('createdBy', '==', uid);
    const unsub = q.onSnapshot(
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => t.appId === app.id);
        if (!isAdmin) rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setTakes(rows);
        setListErr('');
      },
      (err) => setListErr(friendlyError(err))
    );
    return unsub;
  }, [org.id, app.id, uid, isAdmin]);

  const who = (id) => {
    const m = (members || []).find((x) => x.id === id || x.uid === id);
    return m?.email || m?.displayName || 'you';
  };

  return (
    <ModuleFrame.Panel
      title="Record a take"
      meta="This Mac's camera and microphone. The file is saved here for a later model. No voice and no speaking face are generated from it yet."
    >
      <div id="likeness-take">
        <ModuleFrame.Columns cols={2}>
          <Capture
            kind="voice"
            title="Voice"
            cue={VOICE_SCRIPT}
            cueLabel="Read this out loud, in a quiet room, the way you normally talk."
            orgId={org.id}
            appId={app.id}
            uid={uid}
          />
          <Capture
            kind="video"
            title="Face"
            cue={VIDEO_CUE}
            cueLabel="Face the camera. Mouth visible. Ordinary room light is enough."
            orgId={org.id}
            appId={app.id}
            uid={uid}
          />
        </ModuleFrame.Columns>
        <h3 style={{ fontSize: 14, margin: '16px 0 8px' }}>Saved takes</h3>
        {listErr && <p className="cs-form-error" role="alert">{listErr}</p>}
        {takes === null && !listErr && <p className="cs-help">Loading takes…</p>}
        {takes && takes.length === 0 && <p className="cs-help">None yet. Record a voice and a face above.</p>}
        {takes && takes.length > 0 && (
          <ul className="mf-list">
            {takes.map((t) => (
              <TakeRow key={t.id} take={t} who={who(t.createdBy)} orgId={org.id} mine={t.createdBy === uid} />
            ))}
          </ul>
        )}
      </div>
    </ModuleFrame.Panel>
  );
}

function stopStream(stream) {
  stream?.getTracks().forEach((t) => t.stop());
}

function Capture({ kind, title, cue, cueLabel, orgId, appId, uid }) {
  const previewRef = useRef(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(0);
  const capRef = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [seconds, setSeconds] = useState(0);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => () => {
    clearInterval(capRef.current);
    try { recRef.current?.stop(); } catch { /* already stopped */ }
    stopStream(streamRef.current);
  }, []);

  const fail = (e) => {
    const name = e?.name || '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      setErr('The browser blocked the camera or microphone. Allow them for this site, then try again. On a Mac, System Settings → Privacy also has to allow the browser itself.');
    } else if (name === 'NotFoundError') {
      setErr('This Mac did not present a camera or microphone to the browser.');
    } else {
      setErr(friendlyError(e));
    }
    setPhase('idle');
  };

  const release = () => {
    clearInterval(capRef.current);
    stopStream(streamRef.current);
    streamRef.current = null;
    if (previewRef.current) previewRef.current.srcObject = null;
  };

  const start = async () => {
    setErr('');
    setNote('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErr('This browser cannot record. Use Chrome or Safari on this Mac.');
      return;
    }
    const mime = pickMime(kind, (t) => MediaRecorder.isTypeSupported(t));
    if (!mime) {
      setErr('This browser cannot record that media. Use Chrome or Safari on this Mac.');
      return;
    }
    setPhase('arming');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(
        kind === 'voice'
          ? { audio: { echoCancellation: true, noiseSuppression: true }, video: false }
          : { audio: true, video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } } }
      );
    } catch (e) {
      fail(e);
      return;
    }
    streamRef.current = stream;
    if (kind === 'video' && previewRef.current) {
      previewRef.current.srcObject = stream;
      previewRef.current.play?.().catch(() => {});
    }
    chunksRef.current = [];
    let rec;
    try {
      rec = new MediaRecorder(stream, { mimeType: mime });
    } catch {
      try { rec = new MediaRecorder(stream); } catch (e) { release(); fail(e); return; }
    }
    recRef.current = rec;
    rec.ondataavailable = (ev) => { if (ev.data?.size) chunksRef.current.push(ev.data); };
    rec.onstop = () => { upload(mime).catch((e) => { setPhase('idle'); setErr(friendlyError(e)); }); };
    startedRef.current = Date.now();
    setSeconds(0);
    rec.start(500);
    setPhase('recording');
    capRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - startedRef.current) / 1000);
      setSeconds(s);
      if (s >= TAKE_LIMITS[kind]) {
        clearInterval(capRef.current);
        if (rec.state !== 'inactive') rec.stop();
      }
    }, 250);
  };

  const stop = () => {
    clearInterval(capRef.current);
    const rec = recRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  };

  const upload = async (mime) => {
    release();
    const elapsed = Math.max(1, Math.round((Date.now() - startedRef.current) / 1000));
    const type = recRef.current?.mimeType || mime;
    const blob = new Blob(chunksRef.current, { type });
    if (!blob.size) {
      setPhase('idle');
      setErr('The recording was empty. Try again.');
      return;
    }
    setPhase('uploading');
    const plain = String(type).split(';')[0] || 'application/octet-stream';
    const key = takeKey({ orgId, appId, uid, mime: plain });
    const file = new File([blob], key.split('/').pop(), { type: plain });
    await uploadOnce(file, { key, contentType: plain });
    await createEntity('likenessTake', { org: orgId }, {
      appId, kind, key, contentType: plain, bytes: blob.size, seconds: elapsed, createdBy: uid,
    });
    setPhase('idle');
    setNote(`Saved ${elapsed}s.`);
  };

  const limit = TAKE_LIMITS[kind];
  return (
    <div>
      <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>{title}</h3>
      <p className="cs-help" style={{ marginTop: 0 }}>{cueLabel} Stops on its own at {limit}s.</p>
      <p style={{ margin: '0 0 10px', fontSize: 14, lineHeight: 1.45 }}>{cue}</p>
      {kind === 'video' && (
        <video
          ref={previewRef}
          muted
          playsInline
          style={{ width: '100%', maxHeight: 240, background: 'var(--bg-inset)', borderRadius: 8, marginBottom: 8 }}
        />
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {phase === 'recording' ? (
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={stop}>Stop · {seconds}s</button>
        ) : (
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={start} disabled={phase === 'arming' || phase === 'uploading'}>
            {phase === 'uploading' ? 'Saving…' : phase === 'arming' ? 'Asking for access…' : `Record ${title.toLowerCase()}`}
          </button>
        )}
        {phase === 'recording' && <Chip tone="danger">recording</Chip>}
        {note && <span className="cs-help" style={{ margin: 0 }}>{note}</span>}
      </div>
      {err && <p className="cs-form-error" role="alert">{err}</p>}
    </div>
  );
}

function TakeRow({ take, who, orgId, mine }) {
  const [url, setUrl] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const play = async () => {
    setBusy(true);
    setErr('');
    try {
      setUrl(await getAssetDownloadUrl({ key: take.key, name: `${take.kind}.${extFor(take.contentType)}` }));
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm('Delete this take and its recording?')) return;
    try {
      await deleteRemoteObject({ key: take.key });
      await deleteEntity('likenessTake', { org: orgId }, take.id);
    } catch (e) {
      setErr(friendlyError(e));
    }
  };
  const Tag = take.kind === 'video' ? 'video' : 'audio';
  return (
    <li className="mf-row" style={{ alignItems: 'flex-start' }}>
      <span className="mf-row-grow">
        <strong>{take.kind}</strong>
        <span className="mf-muted"> · {who} · {take.seconds || 0}s</span>
        <div style={{ marginTop: 6 }}>
          {!url && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={play} disabled={busy}>{busy ? 'Opening…' : 'Play'}</button>}
          {url && <Tag src={url} controls playsInline style={{ width: '100%', maxHeight: 240, marginTop: 4 }} />}
          {err && <p className="cs-form-error" role="alert">{err}</p>}
        </div>
      </span>
      {mine && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={remove}>Delete</button>}
    </li>
  );
}
